import React, { useState, useEffect, useCallback } from 'react';
import { CssVarsProvider, extendTheme } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import CircularProgress from '@mui/joy/CircularProgress';
import Alert from '@mui/joy/Alert';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import DialogTitle from '@mui/joy/DialogTitle';
import Stack from '@mui/joy/Stack';
import Button from '@mui/joy/Button';
import api from '../lib/api';
import InboxLayout from '../components/inbox/InboxLayout';
import EmailComposeModal from '../components/inbox/EmailComposeModal';
import type { InboxItem } from '../types/inbox';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import useWebSocket from '../hooks/useWebSocket';
import useToast from '../hooks/useToast';
import ToastNotifications, { createInboxToast } from '../components/inbox/ToastNotifications';
import type { InboxUpdatePayload, EmailOpenedPayload } from '../services/websocket';
import { FeatureGate } from '../components/common/FeatureGate';

const theme = extendTheme();

interface InboxApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: InboxItem[];
}

interface SnoozeOption {
  label: string;
  getDate: () => Date;
}

const snoozeOptions: SnoozeOption[] = [
  { label: 'Later Today (3 hours)', getDate: () => new Date(Date.now() + 3 * 60 * 60 * 1000) },
  { label: 'Tomorrow Morning', getDate: () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }},
  { label: 'Tomorrow Afternoon', getDate: () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(14, 0, 0, 0);
    return d;
  }},
  { label: 'Next Week', getDate: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
];

const InboxApp: React.FC = () => {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [snoozeTargetId, setSnoozeTargetId] = useState<number | null>(null);
  const [showSnoozeDialog, setShowSnoozeDialog] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [composeTarget, setComposeTarget] = useState<{ item: InboxItem; address: string } | null>(null);
  
  // Toast notifications
  const { toasts, addToast, removeToast, showSuccess, showError, showInfo } = useToast();

  // Memoize WebSocket callbacks to prevent re-triggering on every render
  const handleWebSocketConnect = useCallback(() => {
    console.log('WebSocket connected for real-time updates');
    showInfo('Connected', 'Real-time updates enabled');
  }, [showInfo]);

  const handleWebSocketDisconnect = useCallback(() => {
    console.log('WebSocket disconnected');
  }, []);

  // WebSocket connection for real-time updates
  const { subscribe } = useWebSocket({
    autoConnect: true,
    onConnect: handleWebSocketConnect,
    onDisconnect: handleWebSocketDisconnect,
  });

  useEffect(() => {
    fetchInboxItems();
  }, []);

  // Subscribe to WebSocket events
  useEffect(() => {
    // Subscribe to new inbox items
    const unsubNewItem = subscribe('new_inbox_item', (data: InboxItem) => {
      // Add new item to list
      setItems(prev => [data, ...prev]);
      
      // Show toast notification
      const toast = createInboxToast(
        data.channel === 'email' ? 'new_email' :
        data.channel === 'sms' ? 'new_sms' :
        data.channel === 'call' ? 'new_call' :
        data.channel === 'task' ? 'new_task' :
        data.channel === 'meeting' ? 'new_meeting' : 'new_email',
        {
          from: data.lead_name || 'Unknown',
          title: data.subject,
        }
      );
      addToast(toast);
    });

    // Subscribe to item updates
    const unsubItemUpdate = subscribe('item_updated', (payload: InboxUpdatePayload) => {
      setItems(prev => prev.map(item => 
        item.id === payload.item_id 
          ? { ...item, ...payload.changes }
          : item
      ));
    });

    // Subscribe to item deletion
    const unsubItemDelete = subscribe('item_deleted', (payload: InboxUpdatePayload) => {
      setItems(prev => prev.filter(item => item.id !== payload.item_id));
    });

    // Inbound SMS: refetch so a new text thread appears without a refresh
    const unsubSmsReceived = subscribe('sms_received', () => {
      fetchInboxItems();
    });

    // Subscribe to email open events
    const unsubEmailOpened = subscribe('email_opened_notification', (payload: EmailOpenedPayload) => {
      const toast = createInboxToast('email_opened', {
        recipient: payload.recipient,
      });
      addToast(toast);
    });

    return () => {
      unsubNewItem();
      unsubItemUpdate();
      unsubItemDelete();
      unsubSmsReceived();
      unsubEmailOpened();
    };
  }, [subscribe, addToast]);

  const fetchInboxItems = async () => {
    setLoading(true);
    setError(null);
    try {
      // Always filter to show only items from known contacts
      const endpoint = '/api/inbox/?has_contact=true';
      
      console.log('Fetching inbox items from:', endpoint);
      const response = await api.get<InboxApiResponse>(endpoint);
      console.log('Full API response object:', response);
      
      // The API client returns the data directly as an array for paginated endpoints
      // Check if response.data is an array (the items) or an object with results
      if (Array.isArray(response.data)) {
        console.log('Setting items from array:', response.data);
        setItems(response.data as InboxItem[]);
        setTotalCount(response.data.length);
        setHasMore(false);
        setNextPageUrl(null);
      } else if (response.data && response.data.results) {
        console.log('Setting items from results:', response.data.results);
        setItems(response.data.results);
        setTotalCount(response.data.count || response.data.results.length);
        setNextPageUrl(response.data.next);
        setHasMore(!!response.data.next);
      } else {
        console.error('Unexpected response structure:', response);
        setItems([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load inbox items';
      setError(errorMessage);
      console.error('Error fetching inbox items:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreItems = async () => {
    if (!hasMore || loadingMore || !nextPageUrl) return;
    
    setLoadingMore(true);
    try {
      // Extract the path from the full URL if needed
      const url = nextPageUrl.startsWith('http') 
        ? new URL(nextPageUrl).pathname + new URL(nextPageUrl).search
        : nextPageUrl;
      
      console.log('Loading more items from:', url);
      const response = await api.get<InboxApiResponse>(url);
      
      if (response.data && response.data.results) {
        // Drop ids already on screen. The ordering tiebreaker on the server
        // should keep pages disjoint, but a row whose priority or timestamp
        // changes between fetches can still straddle a page boundary, and a
        // duplicate here renders as a duplicate row.
        setItems(prev => {
          const seen = new Set(prev.map(item => item.id));
          return [...prev, ...response.data.results.filter(item => !seen.has(item.id))];
        });
        setNextPageUrl(response.data.next);
        setHasMore(!!response.data.next);
      }
    } catch (err) {
      console.error('Error loading more items:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(items.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleItemSelect = (itemId: number, selected: boolean) => {
    const newSelected = new Set(selectedItems);
    if (selected) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleRefresh = () => {
    fetchInboxItems();
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'Escape',
      handler: () => {
        setSelectedItems(new Set());
        setShowSnoozeDialog(false);
      },
      description: 'Clear selection',
    },
    {
      key: 'a',
      ctrlKey: true,
      handler: () => {
        handleSelectAll(true);
      },
      description: 'Select all',
    },
  ]);

  // Bulk operations
  const handleBulkDone = async () => {
    try {
      const itemIds = Array.from(selectedItems);
      await api.post('/api/inbox/bulk-archive/', {
        item_ids: itemIds,
      });
      // Optimistically remove the items from the list
      setItems(prevItems => prevItems.filter(item => !itemIds.includes(item.id)));
      // Update the total count
      setTotalCount(prev => Math.max(0, prev - itemIds.length));
      setSelectedItems(new Set());
      showSuccess('Success', `${itemIds.length} items marked as done`);
    } catch (error) {
      console.error('Failed to bulk archive:', error);
      showError('Error', 'Failed to mark items as done');
      // On error, refetch to ensure consistency
      await fetchInboxItems();
    }
  };

  const handleBulkSnooze = () => {
    if (selectedItems.size > 0) {
      setSnoozeTargetId(null); // null indicates bulk operation
      setShowSnoozeDialog(true);
    }
  };

  const handleSnoozeConfirm = async (option: SnoozeOption) => {
    const until = option.getDate();
    try {
      if (snoozeTargetId) {
        // Single item snooze
        await api.patch(`/api/inbox/${snoozeTargetId}/snooze/`, {
          snooze_until: until.toISOString(),
        });
      } else {
        // Bulk snooze
        await api.post('/api/inbox/bulk-snooze/', {
          item_ids: Array.from(selectedItems),
          snooze_until: until.toISOString(),
        });
        setSelectedItems(new Set());
      }
      await fetchInboxItems();
      setShowSnoozeDialog(false);
      showSuccess('Success', 'Items snoozed');
    } catch (error) {
      console.error('Failed to snooze:', error);
      showError('Error', 'Failed to snooze items');
    }
  };

  const handleItemDone = async (itemId: number) => {
    // Optimistically remove the item from the list
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
    // Update the total count
    setTotalCount(prev => Math.max(0, prev - 1));
  };

  const handleItemSnooze = (itemId: number) => {
    setSnoozeTargetId(itemId);
    setShowSnoozeDialog(true);
  };

  // Resolve a contact's phone number from the linked lead/contact.
  const fetchContactPhone = async (item: InboxItem): Promise<string | null> => {
    if (!item.lead) return null;
    try {
      const response = await api.getContact(item.lead);
      const contact = response.data;
      return contact?.phone || null;
    } catch (err) {
      console.error('Failed to load contact phone:', err);
      return null;
    }
  };

  // Same resolution as the phone: an SMS or call row carries only the contact
  // FK, so the address has to come from the CRM. from_email is the fallback
  // for an email row; to_email is deliberately not — it is our own mailbox.
  const fetchContactEmail = async (item: InboxItem): Promise<string | null> => {
    if (item.lead) {
      try {
        const response = await api.getContact(item.lead);
        // The detail endpoint serializes a secondary address that the shared
        // Contact type doesn't declare; widen rather than give up on it.
        const contact: { email?: string; secondary_email?: string } = response.data;
        const address = contact?.email || contact?.secondary_email;
        if (address) return address;
      } catch (err) {
        console.error('Failed to load contact email:', err);
      }
    }
    return item.from_email || null;
  };

  // Communication actions. Email stays in ApexOS for the same reason as Call
  // and Text below: mailto: dropped the thread, the contact link and the
  // timeline record on the way out to the desktop mail client.
  const handleItemEmail = async (item: InboxItem) => {
    const address = await fetchContactEmail(item);
    if (!address) {
      showError('No email address', `No email address found for ${item.lead_name || 'this contact'}`);
      return;
    }
    setComposeTarget({ item, address });
  };

  // Call and Text both stay inside ApexOS on the org's Twilio number rather
  // than handing off to tel:/sms:, which opened the desktop's phone or
  // Messages app — sending from a personal number, and logging nothing.
  const handleItemCall = async (item: InboxItem) => {
    const phone = await fetchContactPhone(item);
    if (!phone) {
      showError('No phone number', `No phone number found for ${item.lead_name || 'this contact'}`);
      return;
    }
    try {
      await api.post('/api/inbox/call/start/', {
        to_number: phone,
        ...(item.lead ? { lead_id: item.lead } : {}),
      });
      showSuccess(
        'Calling your phone',
        `Answer to be connected to ${item.lead_name || 'the contact'}.`
      );
    } catch (err: any) {
      showError('Call failed', err?.responseData?.error || 'Could not start the call.');
    }
  };

  // InboxLayout expands the row; the expanded SMS view owns the thread and the
  // composer. This only has to surface the "there's nothing to text" case.
  const handleItemText = async (item: InboxItem) => {
    const phone = await fetchContactPhone(item);
    if (!phone) {
      showError('No phone number', `No phone number found for ${item.lead_name || 'this contact'}`);
    }
  };

   
  const handleItemReply = async (itemId: number, content: string, type: 'email' | 'sms') => {
    // Already handled in UnifiedInboxList
    await fetchInboxItems();
  };

  console.log('InboxApp render - items:', items.length, 'loading:', loading);

  if (loading && items.length === 0) {
    return (
      <CssVarsProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </CssVarsProvider>
    );
  }

  return (
    <CssVarsProvider theme={theme}>
      <CssBaseline />
      <ToastNotifications 
        toasts={toasts}
        onRemoveToast={removeToast}
        position="top-right"
      />
      <Box sx={{ height: '100vh', overflow: 'hidden', position: 'relative' }}>
        {error && (
          <Alert color="danger" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        
        <InboxLayout
          items={items}
          loading={loading}
          loadingMore={loadingMore}
          totalCount={totalCount}
          hasMore={hasMore}
          onRefresh={handleRefresh}
          onLoadMore={loadMoreItems}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onItemSelect={handleItemSelect}
          onBulkDone={handleBulkDone}
          onBulkSnooze={handleBulkSnooze}
          onItemDone={handleItemDone}
          onItemSnooze={handleItemSnooze}
          onItemEmail={handleItemEmail}
          onItemCall={handleItemCall}
          onItemText={handleItemText}
          onItemReply={handleItemReply}
        />
        
        {/* Snooze Dialog */}
        <Modal open={showSnoozeDialog} onClose={() => setShowSnoozeDialog(false)}>
          <ModalDialog size="sm">
            <DialogTitle>Snooze Until</DialogTitle>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {snoozeOptions.map((option) => (
                <Button
                  key={option.label}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSnoozeConfirm(option)}
                >
                  {option.label}
                </Button>
              ))}
            </Stack>
          </ModalDialog>
        </Modal>

        {/* In-app email composer, opened by the row's EMAIL action. Mounted
            only while composing so it doesn't load templates on every visit.
            Only an email row has a real subject to answer — an SMS or call
            row's subject is machine-generated ("Missed call from +1…"), which
            as an email subject is worse than leaving it blank. */}
        {composeTarget && (
          <EmailComposeModal
            open
            to={composeTarget.address}
            contactId={composeTarget.item.lead}
            contactName={composeTarget.item.lead_name}
            defaultSubject={
              composeTarget.item.channel === 'email' && composeTarget.item.subject
                ? `Re: ${composeTarget.item.subject}`
                : ''
            }
            onClose={() => setComposeTarget(null)}
            onSent={() => showSuccess('Email sent', 'Your message is on its way.')}
          />
        )}
      </Box>
    </CssVarsProvider>
  );
};

const InboxAppWithGate: React.FC = () => {
  return (
    <FeatureGate
      requiredTiers={['pro', 'premium', 'enterprise']}
      featureName="Unified Inbox"
      featureDescription="Centralize all your communications in one powerful inbox with advanced filtering, bulk actions, and real-time updates."
      benefits={[
        'Unified email and message management',
        'Advanced filtering and search capabilities',
        'Bulk actions for efficient processing',
        'Real-time updates via WebSocket',
        'Snooze and reminder features',
        'Keyboard shortcuts for power users',
        'Conversation threading and tracking'
      ]}
    >
      <InboxApp />
    </FeatureGate>
  );
};

export default InboxAppWithGate;